"use client"

import { useMemo, useState } from "react"
import { FormikProvider, type FormikErrors, useFormik } from "formik"
import { CircleCheck, ExternalLink } from "lucide-react"

import { FormInput, FormSelect } from "@/components/form-inputs"
import { MetaAdsCloseConfirmationDialog } from "@/components/integrations/meta-ads/meta-ads-close-confirmation-dialog"
import { MetaAdsCrmAccessStep } from "@/components/integrations/meta-ads/meta-ads-crm-access-step"
import { MetaAdsFormCopyDialog } from "@/components/integrations/meta-ads/meta-ads-form-copy-dialog"
import { MetaAdsFormReadinessChoice } from "@/components/integrations/meta-ads/meta-ads-form-readiness-choice"
import { MetaAdsLiveAdHandoff } from "@/components/integrations/meta-ads/meta-ads-live-ad-handoff"
import { MetaAdsFollowUpDialog } from "@/components/integrations/meta-ads/meta-ads-follow-up-dialog"
import { MetaAdsTestForm } from "@/components/integrations/meta-ads/meta-ads-test-form"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  useAcknowledgeMetaAdsFormHandoffMutation,
  useBeginMetaAdsOAuthMutation,
  useCheckMetaAdsMutation,
  useCompleteMetaAdsOAuthMutation,
} from "@/hooks/mutations/meta-ads"
import {
  useMetaAdsOAuthAdAccountsQuery,
  useMetaAdsOAuthFormsQuery,
  useMetaAdsOAuthPagesQuery,
  useMetaAdsSetupOptionsQuery,
} from "@/hooks/queries/meta-ads"
import { META_PHONE_COUNTRY_OPTIONS } from "@/lib/meta-ads/country-options"
import {
  getCommonPhoneFieldKeys,
  getFormsInScope,
  getMetaLeadFormReadiness,
} from "@/lib/meta-ads/form-readiness"
import {
  canSendMetaAdsCallbackTest,
  getMetaAdsPostConnectionStep,
  getMetaAdsReadinessFlow,
  type MetaAdsPostConnectionStep,
} from "@/lib/meta-ads/readiness-flow"
import { buildMetaLeadsAccessSettingsUrl } from "@/lib/meta-ads/urls"
import {
  MetaCountryCodeSchema,
  type MetaAdsCompleteOAuthResult,
  type MetaAdsFormCopyResult,
  type MetaAdsFormScope,
  type MetaAdsFormsReadiness,
  type MetaAdsIntegrationView,
  type MetaAdsOAuthAdAccountOption,
  type MetaLeadForm,
  type MetaFollowUpAction,
} from "@/lib/meta-ads/types"
import { openOAuthPopup } from "@/lib/oauth/popup"

type MetaAdsFormValues = {
  platform: "meta_ads"
  firstTaskType: MetaFollowUpAction["actionType"]
  voiceAgentId: string
  callerPhoneNumber: string
  phoneFieldKey: string
  serviceInterestFieldKey: string
  defaultPhoneCountry: string
  respectWorkingHours: boolean
  consentConfirmed: boolean
  pageId: string
  adAccountId: string
  formScope: MetaAdsFormScope
  selectedFormIds: string[]
}

const EMPTY_FORMS_READINESS: MetaAdsFormsReadiness = {
  forms: [],
  leadAccessStatus: "unverified",
  leadAccessErrorCode: null,
}

export function MetaAdsSetupDialog({
  open,
  integration,
  onOpenChange,
  initialVoiceAgentId,
}: {
  open: boolean
  integration: MetaAdsIntegrationView | null
  onOpenChange: (open: boolean) => void
  /** Used when setup starts from a specific Voice Agent. Existing settings always win. */
  initialVoiceAgentId?: string
}) {
  const beginMutation = useBeginMetaAdsOAuthMutation()
  const completeMutation = useCompleteMetaAdsOAuthMutation()
  const checkMutation = useCheckMetaAdsMutation()
  const acknowledgeHandoffMutation = useAcknowledgeMetaAdsFormHandoffMutation()
  const optionsQuery = useMetaAdsSetupOptionsQuery(open)
  const [authorizedSessionId, setAuthorizedSessionId] = useState<string | null>(null)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [completionResult, setCompletionResult] = useState<MetaAdsCompleteOAuthResult | null>(null)
  const [copySourceForm, setCopySourceForm] = useState<MetaLeadForm | null>(null)
  const [closeConfirmationOpen, setCloseConfirmationOpen] = useState(false)
  const [followUpOpen, setFollowUpOpen] = useState(false)
  const pagesQuery = useMetaAdsOAuthPagesQuery(authorizedSessionId)
  const adAccountsQuery = useMetaAdsOAuthAdAccountsQuery(authorizedSessionId)
  const initialValues = useMemo(
    () => createInitialValues(integration, initialVoiceAgentId),
    [initialVoiceAgentId, integration],
  )
  const formik = useFormik<MetaAdsFormValues>({
    initialValues,
    enableReinitialize: true,
    validate: validateMetaAdsForm,
    onSubmit: async (values, helpers) => {
      setConnectionError(null)
      try {
        const result = await beginMutation.mutateAsync({
          voiceAgentId: values.firstTaskType === "schedule_call" ? values.voiceAgentId || null : null,
          callerPhoneNumber: values.firstTaskType === "schedule_call" ? values.callerPhoneNumber || null : null,
          phoneFieldKey: values.phoneFieldKey,
          serviceInterestFieldKey: values.serviceInterestFieldKey.trim() || null,
          defaultPhoneCountry: MetaCountryCodeSchema.parse(
            values.defaultPhoneCountry.trim().toUpperCase(),
          ),
          respectWorkingHours: values.respectWorkingHours,
          setErrors: errors => helpers.setErrors(errors as FormikErrors<MetaAdsFormValues>),
        })
        openMetaAuthorization(result.authorizeUrl, result.sessionId)
      } catch (error: unknown) {
        setConnectionError(getConnectionErrorMessage(error))
      }
    },
  })
  const formsQuery = useMetaAdsOAuthFormsQuery(
    authorizedSessionId,
    formik.values.pageId,
  )

  function openMetaAuthorization(authorizeUrl: string, expectedSessionId: string) {
    openOAuthPopup({
      url: authorizeUrl,
      name: "versafy-meta-ads-oauth",
      width: 680,
      height: 760,
      acceptMessage: data => data.meta_ads_oauth === "authorized"
        || data.meta_ads_oauth === "failed",
      onMessage: data => {
        if (data.error) {
          setConnectionError(metaOAuthErrorMessage(String(data.error)))
          return
        }
        const sessionId = String(data.session_id ?? "")
        if (sessionId !== expectedSessionId) {
          setConnectionError("Meta returned an unexpected authorization session. Please try again.")
          return
        }
        setAuthorizedSessionId(sessionId)
      },
      onError: message => setConnectionError(message),
    })
  }

  async function completeConnection() {
    setConnectionError(null)
    if (!authorizedSessionId || !formik.values.pageId) return
    if (!formik.values.adAccountId) {
      setConnectionError("Choose the Meta ad account that contains this Page's live ad.")
      return
    }
    if (
      formik.values.formScope === "selected"
      && formik.values.selectedFormIds.length === 0
    ) {
      setConnectionError("Select at least one Instant Form.")
      return
    }
    try {
      const result = await completeMutation.mutateAsync({
        sessionId: authorizedSessionId,
        pageId: formik.values.pageId,
        adAccountId: formik.values.adAccountId,
        phoneFieldKey: formik.values.phoneFieldKey,
        formScope: formik.values.formScope,
        selectedFormIds: formik.values.formScope === "selected"
          ? formik.values.selectedFormIds
          : [],
      })
      setCompletionResult(result)
      setFollowUpOpen(true)
    } catch (error: unknown) {
      setConnectionError(getConnectionErrorMessage(error))
    }
  }

  function handleDialogOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      onOpenChange(true)
      return
    }
    requestClose()
  }

  function requestClose() {
    setCloseConfirmationOpen(true)
  }

  function closeDialog() {
    setCloseConfirmationOpen(false)
    setConnectionError(null)
    setAuthorizedSessionId(null)
    setCompletionResult(null)
    setFollowUpOpen(false)
    setCopySourceForm(null)
    formik.resetForm()
    onOpenChange(false)
  }

  async function checkReadinessAgain() {
    setConnectionError(null)
    try {
      const integrationResult = await checkMutation.mutateAsync()
      setCompletionResult(current => current ? {
        ...current,
        integration: integrationResult,
        readiness: {
          ...current.readiness,
          leadAccessStatus: getLeadAccessStatus(integrationResult.last_error_code),
          leadAccessErrorCode: integrationResult.last_error_code === "meta_lead_access_unverified"
            ? "meta_lead_access_unverified"
            : null,
        },
      } : current)
    } catch (error: unknown) {
      setConnectionError(getConnectionErrorMessage(error))
    }
  }

  async function acknowledgeLiveAdHandoffs(copiedFormIds: string[]) {
    setConnectionError(null)
    try {
      for (const copiedFormId of copiedFormIds) {
        const integrationResult = await acknowledgeHandoffMutation.mutateAsync(copiedFormId)
        setCompletionResult(current => current ? {
          ...current,
          integration: integrationResult,
        } : current)
      }
    } catch (error: unknown) {
      setConnectionError(getConnectionErrorMessage(error))
    }
  }

  function handleCopyCreated(result: MetaAdsFormCopyResult) {
    setCompletionResult(current => current ? {
      ...current,
      integration: result.integration,
      readiness: {
        ...current.readiness,
        forms: [
          ...current.readiness.forms.filter(form => form.id !== result.copiedForm.id),
          result.copiedForm,
        ],
      },
    } : current)
  }

  return (
    <>
      <Dialog open={open && !followUpOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-h-[90vh] overscroll-contain overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{integration ? "Reconnect Meta Ads" : "Connect an Ads Platform"}</DialogTitle>
          <DialogDescription>
            Connect a Page and choose the first task. Configure an optional fallback for each Instant Form after connecting.
          </DialogDescription>
        </DialogHeader>

        <FormikProvider value={formik}>
          {connectionError ? (
            <Alert variant="destructive" role="alert">
              <AlertTitle>Meta Ads could not be connected</AlertTitle>
              <AlertDescription>{connectionError}</AlertDescription>
            </Alert>
          ) : null}
          {completionResult ? (
            <MetaPostConnectionReadiness
              result={completionResult}
              phoneFieldKey={formik.values.phoneFieldKey}
              isChecking={checkMutation.isPending}
              isAcknowledging={acknowledgeHandoffMutation.isPending}
              onCopy={setCopySourceForm}
              onCheck={() => void checkReadinessAgain()}
              onAcknowledge={copiedFormIds => void acknowledgeLiveAdHandoffs(copiedFormIds)}
              onTestSent={closeDialog}
            />
          ) : authorizedSessionId ? (
            <MetaPageSelection
              isLoading={pagesQuery.isLoading}
              hasError={Boolean(pagesQuery.error)}
              pages={pagesQuery.data ?? []}
              adAccounts={adAccountsQuery.data ?? []}
              selectedPageId={formik.values.pageId}
              selectedAdAccountId={formik.values.adAccountId}
              adAccountsLoading={adAccountsQuery.isLoading}
              adAccountsError={Boolean(adAccountsQuery.error)}
              formScope={formik.values.formScope}
              readiness={formsQuery.data ?? EMPTY_FORMS_READINESS}
              formsLoading={formsQuery.isLoading}
              formsError={Boolean(formsQuery.error)}
              selectedFormIds={formik.values.selectedFormIds}
              isSaving={completeMutation.isPending}
              onRetry={() => void pagesQuery.refetch()}
              onRetryAdAccounts={() => void adAccountsQuery.refetch()}
              onRetryForms={() => void formsQuery.refetch()}
              onPageChange={pageId => {
                if (pageId !== integration?.meta_page_id) {
                  void formik.setFieldValue("selectedFormIds", [])
                }
              }}
              onAdAccountChange={adAccountId => {
                void formik.setFieldValue("adAccountId", adAccountId)
              }}
              onFormScopeChange={scope => {
                void formik.setFieldValue("formScope", scope)
                if (scope === "all") void formik.setFieldValue("selectedFormIds", [])
              }}
              onSelectedFormsChange={formIds => {
                void formik.setFieldValue("selectedFormIds", formIds)
              }}
              phoneFieldKey={formik.values.phoneFieldKey}
              requiresPhoneField={formik.values.firstTaskType !== "send_email"}
              onPhoneFieldKeyChange={key => void formik.setFieldValue("phoneFieldKey", key)}
              onCancel={requestClose}
              onComplete={() => void completeConnection()}
            />
          ) : (
            <form onSubmit={formik.handleSubmit} className="space-y-5">
              <FormSelect
                name="platform"
                label="Ad platform"
                options={[{ value: "meta_ads", label: "Meta Ads" }]}
                disabled
              />

              <Alert>
                <AlertTitle>Connect securely with Meta</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    Sign in with the personal Facebook account that manages the business Page used by your ads. The account must be allowed to connect apps for that Page and access its leads. If the Page belongs to a Business Portfolio, make sure the account is assigned to the Page and has lead access.
                  </p>
                  <p>
                    Approve access to the relevant Business Portfolio and Pages when Meta asks. You will then select a Page and configure how its Instant Form leads are contacted. You do not need to create a Developer App, copy API keys, or configure a webhook.
                  </p>
                  <a
                    className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
                    href="https://docs.versafy.ai/integrations/meta-ads"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Read the Meta Ads connection guide</span>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <p className="text-xs">
                    <span>If you have any questions or get stuck, click</span> <strong>Ask AI</strong> <span>in the bottom-right corner of the page.</span>
                  </p>
                </AlertDescription>
              </Alert>

              <FormSelect name="firstTaskType" label="First task for each lead" options={[
                { value: "schedule_call", label: "Schedule Call" },
                { value: "send_email", label: "Send Email" },
                { value: "send_whatsapp", label: "Send WhatsApp" },
              ]} description="After connecting, choose the agent, sender, form fields, and optional fallback for each Instant Form." />

              {formik.values.firstTaskType === "schedule_call" ? <div className="grid gap-4 sm:grid-cols-2">
                <FormSelect
                  name="voiceAgentId"
                  label="Callback Voice Agent"
                  placeholder="Select an active agent"
                  disabled={optionsQuery.isLoading}
                  options={(optionsQuery.data?.voiceAgents ?? []).map(agent => ({
                    value: agent.id,
                    label: agent.name,
                  }))}
                />
                <FormSelect
                  name="callerPhoneNumber"
                  label="Caller number"
                  placeholder="Select an outbound number"
                  disabled={optionsQuery.isLoading}
                  options={(optionsQuery.data?.phoneNumbers ?? []).map(number => ({
                    value: number.phoneNumber,
                    label: number.label
                      ? `${number.label} (${number.phoneNumber})`
                      : number.phoneNumber,
                  }))}
                />
              </div> : null}

              {optionsQuery.error && formik.values.firstTaskType === "schedule_call" ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    Voice Agents and caller numbers could not be loaded. Close the dialog and try again.
                  </AlertDescription>
                </Alert>
              ) : null}

              {formik.values.firstTaskType === "schedule_call" ? <div className="grid gap-4 sm:grid-cols-2">
                <FormInput
                  name="phoneFieldKey"
                  label="Phone field key"
                  description="This must match the phone field in your Instant Form, such as phone_number."
                />
                <FormInput
                  name="serviceInterestFieldKey"
                  label="Service interest field key (optional)"
                  description="Add a custom form field key if the agent should know what the lead selected."
                />
              </div> : null}

              {formik.values.firstTaskType === "schedule_call" ? <FormSelect
                name="defaultPhoneCountry"
                label="Default phone country"
                options={META_PHONE_COUNTRY_OPTIONS}
                description="Used only when a lead submits a locally formatted number without a country code."
              /> : null}

              {formik.values.firstTaskType === "schedule_call" ? <CheckboxRow
                id="meta-working-hours"
                checked={formik.values.respectWorkingHours}
                onCheckedChange={checked => void formik.setFieldValue("respectWorkingHours", checked === true)}
                title="Keep callbacks inside organization working hours"
                description="Leads received outside working hours are scheduled for the next available opening."
              /> : null}

              <CheckboxRow
                id="meta-consent"
                checked={formik.values.consentConfirmed}
                onCheckedChange={checked => void formik.setFieldValue("consentConfirmed", checked === true)}
                title="I confirm our lead form and follow-up process meet our consent and privacy obligations"
                description="Your organization is responsible for its form disclosure and contact requirements."
                error={formik.touched.consentConfirmed ? formik.errors.consentConfirmed : undefined}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={requestClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formik.isSubmitting || (formik.values.firstTaskType === "schedule_call" && optionsQuery.isLoading)}>
                  {formik.isSubmitting ? "Preparing…" : "Continue with Meta"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </FormikProvider>
      </DialogContent>
      </Dialog>
      {completionResult ? <MetaAdsFollowUpDialog
        open={followUpOpen}
        onOpenChange={setFollowUpOpen}
        integration={completionResult.integration}
        initialPrimaryActionType={formik.values.firstTaskType}
        onSaved={updated => setCompletionResult(current => current ? { ...current, integration: updated } : current)}
      /> : null}
      <MetaAdsCloseConfirmationDialog
        open={closeConfirmationOpen}
        connectionSaved={Boolean(integration || completionResult)}
        onOpenChange={setCloseConfirmationOpen}
        onConfirm={closeDialog}
      />
      <MetaAdsFormCopyDialog
        sourceForm={copySourceForm}
        onOpenChange={nextOpen => {
          if (!nextOpen) setCopySourceForm(null)
        }}
        onCreated={handleCopyCreated}
      />
    </>
  )
}

function MetaPageSelection({
  isLoading,
  hasError,
  pages,
  adAccounts,
  selectedPageId,
  selectedAdAccountId,
  adAccountsLoading,
  adAccountsError,
  formScope,
  readiness,
  formsLoading,
  formsError,
  selectedFormIds,
  isSaving,
  onRetry,
  onRetryAdAccounts,
  onRetryForms,
  onPageChange,
  onAdAccountChange,
  onFormScopeChange,
  onSelectedFormsChange,
  phoneFieldKey,
  requiresPhoneField,
  onPhoneFieldKeyChange,
  onCancel,
  onComplete,
}: {
  isLoading: boolean
  hasError: boolean
  pages: Array<{ id: string; name: string }>
  adAccounts: MetaAdsOAuthAdAccountOption[]
  selectedPageId: string
  selectedAdAccountId: string
  adAccountsLoading: boolean
  adAccountsError: boolean
  formScope: MetaAdsFormScope
  readiness: MetaAdsFormsReadiness
  formsLoading: boolean
  formsError: boolean
  selectedFormIds: string[]
  isSaving: boolean
  onRetry: () => void
  onRetryAdAccounts: () => void
  onRetryForms: () => void
  onPageChange: (pageId: string) => void
  onAdAccountChange: (adAccountId: string) => void
  onFormScopeChange: (scope: MetaAdsFormScope) => void
  onSelectedFormsChange: (formIds: string[]) => void
  phoneFieldKey: string
  requiresPhoneField: boolean
  onPhoneFieldKeyChange: (key: string) => void
  onCancel: () => void
  onComplete: () => void
}) {
  const forms = readiness.forms
  const formsInScope = getFormsInScope(forms, formScope, selectedFormIds)
  const detectedPhoneKeys = getDetectedPhoneKeys(formsInScope)

  return (
    <div className="space-y-5">
      <Alert>
        <AlertTitle>Meta authorization complete</AlertTitle>
        <AlertDescription>
          Choose the Facebook Page whose Instant Form leads should create tasks. Versafy will subscribe it automatically.
        </AlertDescription>
      </Alert>
      {isLoading ? <div className="h-20 animate-pulse rounded-lg bg-muted motion-reduce:animate-none" /> : null}
      {hasError ? (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>Authorized Pages could not be loaded.</span>
            <Button type="button" size="sm" variant="outline" onClick={onRetry}>Retry</Button>
          </AlertDescription>
        </Alert>
      ) : null}
      {!isLoading && !hasError && pages.length === 0 ? (
        <Alert variant="destructive">
          <AlertDescription>
            No Facebook Pages were authorized. Start again and select a Page that you can manage and access leads for.
          </AlertDescription>
        </Alert>
      ) : null}
      {pages.length > 0 ? (
        <FormSelect
          name="pageId"
          label="Facebook Page"
          placeholder="Select an authorized Page"
          options={pages.map(page => ({ value: page.id, label: page.name }))}
          onValueChange={onPageChange}
        />
      ) : null}
      {selectedPageId ? (
        <FormSelect
          name="adAccountId"
          label="Ad account containing this Page's ad"
          placeholder="Select the correct ad account"
          description="This opens Ads Manager in the right business and ad account instead of your personal default."
          disabled={adAccountsLoading || adAccountsError}
          options={adAccounts.map(account => ({
            value: account.id,
            label: account.businessName
              ? `${account.name} — ${account.businessName}`
              : `${account.name} — Personal ad account`,
          }))}
          onValueChange={onAdAccountChange}
        />
      ) : null}
      {selectedPageId && adAccountsError ? (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>Authorized ad accounts could not be loaded.</span>
            <Button type="button" size="sm" variant="outline" onClick={onRetryAdAccounts}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
      {selectedPageId && !adAccountsLoading && !adAccountsError && adAccounts.length === 0 ? (
        <Alert variant="destructive">
          <AlertDescription>
            No Meta ad accounts were authorized. Reconnect and approve the business portfolio
            that owns this Page&apos;s live ad.
          </AlertDescription>
        </Alert>
      ) : null}
      {selectedPageId ? (
        <FormSelect
          name="formScope"
          label="Which Instant Forms should create lead tasks?"
          description="Include every current and future form on this Page, or choose specific forms."
          options={[
            { value: "all", label: "All Instant Forms" },
            { value: "selected", label: "Selected Instant Forms" },
          ]}
          onValueChange={onFormScopeChange}
        />
      ) : null}
      {selectedPageId ? (
        <InstantFormReadinessList
          forms={forms}
          isLoading={formsLoading}
          hasError={formsError}
          selectable={formScope === "selected"}
          phoneFieldKey={requiresPhoneField ? phoneFieldKey : undefined}
          selectedFormIds={selectedFormIds}
          onRetry={onRetryForms}
          onChange={onSelectedFormsChange}
        />
      ) : null}
      {requiresPhoneField && selectedPageId && !formsLoading && !formsError && formsInScope.length > 0 ? (
        <div className="space-y-3">
          {detectedPhoneKeys.length > 0 ? (
            <FormSelect
              name="phoneFieldKey"
              label="Phone field"
              description="Versafy checks this field against every form selected above."
              options={detectedPhoneKeys.map(key => ({ value: key, label: key }))}
              onValueChange={onPhoneFieldKeyChange}
            />
          ) : (
            <FormInput
              name="phoneFieldKey"
              label="Phone field key"
              description="No phone question was detected. Enter a field key to include when creating a compatible copy."
            />
          )}
          <PhoneFieldSummary forms={formsInScope} phoneFieldKey={phoneFieldKey} />
        </div>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button
          type="button"
          disabled={
            isSaving
            || pages.length === 0
            || !selectedPageId
            || !selectedAdAccountId
            || adAccountsLoading
            || adAccountsError
            || formsLoading
            || formsError
            || (formScope === "selected" && (
              selectedFormIds.length === 0
            ))
          }
          onClick={onComplete}
        >
          {isSaving ? "Saving…" : "Save Page and continue"}
        </Button>
      </DialogFooter>
    </div>
  )
}

function InstantFormReadinessList({
  forms,
  isLoading,
  hasError,
  selectable,
  phoneFieldKey,
  selectedFormIds,
  onRetry,
  onChange,
}: {
  forms: MetaLeadForm[]
  isLoading: boolean
  hasError: boolean
  selectable: boolean
  phoneFieldKey?: string
  selectedFormIds: string[]
  onRetry: () => void
  onChange: (formIds: string[]) => void
}) {
  if (isLoading) {
    return <div className="h-24 animate-pulse rounded-lg bg-muted motion-reduce:animate-none" />
  }
  if (hasError) {
    return (
      <Alert variant="destructive">
        <AlertDescription className="flex items-center justify-between gap-3">
          <span>Instant Forms could not be loaded from this Page.</span>
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>Retry</Button>
        </AlertDescription>
      </Alert>
    )
  }
  if (forms.length === 0) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          This Page has no published Instant Forms available for selection.
        </AlertDescription>
      </Alert>
    )
  }
  return (
    <fieldset className="space-y-2 rounded-lg border p-4">
      <legend className="px-1 text-sm font-medium">
        {selectable ? "Select one or more Instant Forms" : "Instant Form readiness"}
      </legend>
      <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
        {forms.map(form => {
          const checked = selectedFormIds.includes(form.id)
          const inputId = `meta-form-${form.id}`
          return (
            <Label
              key={form.id}
              htmlFor={selectable ? inputId : undefined}
              className={`flex items-start gap-3 rounded-md border p-3 font-normal ${selectable ? "cursor-pointer hover:bg-muted" : ""}`}
            >
              {selectable ? (
                <Checkbox
                  id={inputId}
                  checked={checked}
                  onCheckedChange={() => {
                    onChange(checked
                      ? selectedFormIds.filter(formId => formId !== form.id)
                      : [...selectedFormIds, form.id])
                  }}
                />
              ) : null}
              <div className="min-w-0 flex-1 space-y-2">
                <span className="block truncate">{form.name}</span>
                <FormReadinessBadges form={form} phoneFieldKey={phoneFieldKey} />
              </div>
            </Label>
          )
        })}
      </div>
      {selectable ? (
        <p className="text-xs text-muted-foreground">
          {selectedFormIds.length === 0
            ? "Select at least one form."
            : `${selectedFormIds.length} form${selectedFormIds.length === 1 ? "" : "s"} selected.`}
        </p>
      ) : null}
    </fieldset>
  )
}

function MetaPostConnectionReadiness({
  result,
  phoneFieldKey,
  isChecking,
  isAcknowledging,
  onCopy,
  onCheck,
  onAcknowledge,
  onTestSent,
}: {
  result: MetaAdsCompleteOAuthResult
  phoneFieldKey: string
  isChecking: boolean
  isAcknowledging: boolean
  onCopy: (form: MetaLeadForm) => void
  onCheck: () => void
  onAcknowledge: (copiedFormIds: string[]) => void
  onTestSent: () => void
}) {
  const [crmAccessConfirmed, setCrmAccessConfirmed] = useState(false)
  const [skippedCopyFormIds, setSkippedCopyFormIds] = useState<string[]>([])
  const formsInScope = getFormsInScope(
    result.readiness.forms,
    result.integration.form_scope,
    result.integration.selected_form_ids,
  )
  const {
    formsNeedingCopy,
    visibleHandoffs,
  } = getMetaAdsReadinessFlow({
    formsInScope,
    handoffs: result.integration.form_handoffs,
    phoneFieldKey,
    requiresPhoneField: Boolean(result.integration.voice_agent_id),
  })
  const currentFormNeedingCopy = formsNeedingCopy.find(form => (
    !skippedCopyFormIds.includes(form.id)
  )) ?? null
  const pendingHandoffs = visibleHandoffs.filter(handoff => !handoff.acknowledgedAt)
  const readyNextStep = getMetaAdsPostConnectionStep({
    hasForms: formsInScope.length > 0,
    needsCopy: Boolean(currentFormNeedingCopy),
    hasPendingHandoff: pendingHandoffs.length > 0,
    crmAccessConfirmed,
  })
  const canSkipCurrentCopy = currentFormNeedingCopy
    ? !result.integration.voice_agent_id || getMetaLeadFormReadiness(currentFormNeedingCopy, phoneFieldKey)
      .configuredPhoneFieldPresent
    : false
  return (
    <div className="space-y-5" aria-live="polite">
      <Alert>
        <CircleCheck aria-hidden="true" className="h-4 w-4" />
        <AlertTitle>Your Facebook Page is connected</AlertTitle>
        <AlertDescription>{getReadyNextStepDescription(readyNextStep)}</AlertDescription>
      </Alert>

      {formsInScope.length === 0 ? (
        <Alert variant="destructive">
          <AlertDescription>
            No active Instant Forms are currently covered by this connection.
          </AlertDescription>
        </Alert>
      ) : null}

      {readyNextStep === "create_copy" && currentFormNeedingCopy ? (
        <MetaAdsFormReadinessChoice
          form={currentFormNeedingCopy}
          phoneFieldKey={phoneFieldKey}
        />
      ) : null}

      {readyNextStep === "update_ad" ? (
        <MetaAdsLiveAdHandoff
          handoffs={pendingHandoffs}
          adAccountName={result.integration.meta_ad_account_name}
        />
      ) : null}

      {readyNextStep === "assign_crm" ? (
        <MetaAdsCrmAccessStep
          businessName={result.integration.meta_business_name}
          businessSettingsUrl={buildMetaLeadsAccessSettingsUrl(
            result.integration.meta_business_id,
          )}
          pageName={result.integration.meta_page_name}
          onConfirmed={() => setCrmAccessConfirmed(true)}
        />
      ) : null}

      {readyNextStep === "test_connection" ? (
        <section className="space-y-4" aria-labelledby="meta-ads-test-step-title">
          <div>
            <h3 id="meta-ads-test-step-title" className="font-semibold">
              Step 4: Test the follow-up
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {canSendMetaAdsCallbackTest(result.integration)
                ? "Send one test lead through Meta and confirm that Versafy creates its callback task."
                : "Submit your own details through the live Instant Form to test Email or WhatsApp. Save its first task and fallback before testing; the built-in test lead supplies only a phone number."}
            </p>
          </div>
          {canSendMetaAdsCallbackTest(result.integration) ? <MetaAdsTestForm
            cancelLabel="Test later"
            footerClassName="sticky bottom-0 z-10 -mx-6 -mb-6 border-t bg-background px-6 py-4"
            onCancel={onTestSent}
            onSuccess={onTestSent}
          /> : <Button type="button" variant="outline" onClick={onTestSent}>Done</Button>}
        </section>
      ) : null}

      {readyNextStep === "check_forms"
        || readyNextStep === "create_copy"
        || readyNextStep === "update_ad" ? (
        <DialogFooter className="sticky bottom-0 z-10 -mx-6 -mb-6 border-t bg-background px-6 py-4">
          {readyNextStep === "check_forms" ? (
            <Button type="button" variant="outline" disabled={isChecking} onClick={onCheck}>
              {isChecking ? "Checking…" : "Check again"}
            </Button>
          ) : null}
          {currentFormNeedingCopy ? (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={!canSkipCurrentCopy}
                onClick={() => setSkippedCopyFormIds(current => (
                  currentFormNeedingCopy
                    ? [...current, currentFormNeedingCopy.id]
                    : current
                ))}
              >
                Skip Copy & Use Live Ads Only
              </Button>
              <Button type="button" onClick={() => onCopy(currentFormNeedingCopy)}>
                Create Unrestricted Copy
              </Button>
            </>
          ) : null}
          {readyNextStep === "update_ad" ? (
            <>
              <Button asChild type="button" variant="outline">
                <a
                  href={result.integration.ads_manager_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>Open Ads Manager</span>
                  <ExternalLink aria-hidden="true" className="ml-2 h-3.5 w-3.5" />
                </a>
              </Button>
              <Button
                type="button"
                disabled={isAcknowledging}
                onClick={() => onAcknowledge(
                  pendingHandoffs.map(handoff => handoff.copiedForm.id),
                )}
              >
                {isAcknowledging
                  ? "Saving…"
                  : `I Updated My Ad${pendingHandoffs.length === 1 ? "" : "s"}`}
              </Button>
            </>
          ) : null}
        </DialogFooter>
      ) : null}
    </div>
  )
}

function getReadyNextStepDescription(nextStep: MetaAdsPostConnectionStep): string {
  switch (nextStep) {
    case "check_forms":
      return "No active Instant Forms are available yet. Check again after publishing one in Meta."
    case "create_copy":
      return "Page access and the lead webhook are ready. Complete Step 1 below."
    case "update_ad":
      return "Your unrestricted copy is ready. Complete Step 2 below."
    case "assign_crm":
      return "Your forms are ready. Complete Step 3 below so Meta can deliver their leads."
    case "test_connection":
      return "CRM lead access is confirmed. Complete Step 4 below."
    default: {
      const exhaustiveStep: never = nextStep
      return exhaustiveStep
    }
  }
}

function PhoneFieldSummary({
  forms,
  phoneFieldKey,
}: {
  forms: MetaLeadForm[]
  phoneFieldKey: string
}) {
  const missingForms = forms.filter(form => !getMetaLeadFormReadiness(
    form,
    phoneFieldKey,
  ).configuredPhoneFieldPresent)
  if (missingForms.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-emerald-700">
        <CircleCheck aria-hidden="true" className="h-4 w-4" />
        <span>The</span> <strong>{phoneFieldKey}</strong> <span>phone field is present on every form in scope.</span>
      </p>
    )
  }
  return (
    <Alert variant="destructive">
      <AlertDescription>
        <span>The</span> <strong>{phoneFieldKey}</strong> <span>phone field is missing from</span> {missingForms.length}
        {" "}<span>form</span><span>{missingForms.length === 1 ? "" : "s"}</span><span>:</span> {missingForms.map(form => form.name).join(", ")}<span>.
        You can save the Page and create compatible copies on the next step.</span>
      </AlertDescription>
    </Alert>
  )
}

function FormReadinessBadges({
  form,
  phoneFieldKey,
}: {
  form: MetaLeadForm
  phoneFieldKey?: string
}) {
  const readiness = getMetaLeadFormReadiness(form, phoneFieldKey ?? "")
  return (
    <div className="flex flex-wrap gap-2">
      <DeliveryBadge deliveryMode={readiness.deliveryMode} />
      {phoneFieldKey && readiness.configuredPhoneFieldPresent ? (
        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
          Phone field ready
        </Badge>
      ) : phoneFieldKey ? (
        <Badge variant="destructive">Phone field missing</Badge>
      ) : null}
    </div>
  )
}

function DeliveryBadge({
  deliveryMode,
}: {
  deliveryMode: ReturnType<typeof getMetaLeadFormReadiness>["deliveryMode"]
}) {
  switch (deliveryMode) {
    case "all_submissions":
      return (
        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
          Shared links and ads ready
        </Badge>
      )
    case "targeted_ads_only":
      return <Badge variant="secondary">Targeted ads only</Badge>
    case "unknown":
      return <Badge variant="outline">Delivery mode not verified</Badge>
    default: {
      const exhaustiveMode: never = deliveryMode
      return exhaustiveMode
    }
  }
}

function getDetectedPhoneKeys(forms: MetaLeadForm[]): string[] {
  const commonKeys = getCommonPhoneFieldKeys(forms)
  if (commonKeys.length > 0) return commonKeys
  return [...new Set(forms.flatMap(form => getMetaLeadFormReadiness(
    form,
    "phone_number",
  ).phoneFieldKeys))].sort()
}

function getLeadAccessStatus(
  errorCode: string | null,
): MetaAdsFormsReadiness["leadAccessStatus"] {
  switch (errorCode) {
    case "meta_lead_access_required":
      return "action_required"
    case "meta_lead_access_unverified":
      return "unverified"
    default:
      return "ready"
  }
}

function CheckboxRow({
  id,
  checked,
  onCheckedChange,
  title,
  description,
  error,
}: {
  id: string
  checked: boolean
  onCheckedChange: (checked: boolean | "indeterminate") => void
  title: string
  description: string
  error?: string
}) {
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-start gap-3">
        <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} className="mt-0.5" />
        <div className="space-y-1">
          <Label htmlFor={id}>{title}</Label>
          <p className="text-xs text-muted-foreground">{description}</p>
          {error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}
        </div>
      </div>
    </div>
  )
}

function createInitialValues(
  integration: MetaAdsIntegrationView | null,
  initialVoiceAgentId?: string,
): MetaAdsFormValues {
  const savedActions = integration
    ? Object.values(integration.form_action_overrides).find(actions => actions.length > 0)
    : undefined
  return {
    platform: "meta_ads",
    firstTaskType: savedActions?.[0]?.actionType
      ?? (integration && !integration.voice_agent_id ? "send_email" : "schedule_call"),
    voiceAgentId: integration?.voice_agent_id ?? initialVoiceAgentId ?? "",
    callerPhoneNumber: integration?.caller_phone_number ?? "",
    phoneFieldKey: integration?.phone_field_key ?? "phone_number",
    serviceInterestFieldKey: integration?.service_interest_field_key ?? "",
    defaultPhoneCountry: integration?.default_phone_country ?? "DE",
    respectWorkingHours: integration?.respect_working_hours ?? true,
    consentConfirmed: false,
    pageId: "",
    adAccountId: integration?.meta_ad_account_id ?? "",
    formScope: integration?.form_scope ?? "all",
    selectedFormIds: integration?.selected_form_ids ?? [],
  }
}

function validateMetaAdsForm(values: MetaAdsFormValues): FormikErrors<MetaAdsFormValues> {
  const errors: FormikErrors<MetaAdsFormValues> = {}
  if (values.firstTaskType === "schedule_call") {
    if (!values.voiceAgentId) errors.voiceAgentId = "Select a callback Voice Agent."
    if (!values.callerPhoneNumber) errors.callerPhoneNumber = "Select a caller number."
  }
  if (!/^[A-Za-z0-9_]{1,100}$/.test(values.phoneFieldKey)) {
    errors.phoneFieldKey = "Use the field key shown in Meta."
  }
  if (values.serviceInterestFieldKey && !/^[A-Za-z0-9_]{1,100}$/.test(values.serviceInterestFieldKey)) {
    errors.serviceInterestFieldKey = "Use the field key shown in Meta."
  }
  if (!MetaCountryCodeSchema.safeParse(values.defaultPhoneCountry).success) {
    errors.defaultPhoneCountry = "Select a supported country."
  }
  if (!values.consentConfirmed) errors.consentConfirmed = "Confirm this before continuing."
  return errors
}

function metaOAuthErrorMessage(code: string): string {
  switch (code) {
    case "oauth_denied":
      return "Meta authorization was cancelled."
    case "missing_permissions":
      return "Meta did not grant all required access. Approve the relevant Business Portfolio, Page, and lead permissions, then try again."
    case "no_eligible_pages":
      return "No Facebook Pages were authorized for this connection."
    case "invalid_or_expired_state":
      return "The Meta connection expired. Please start again."
    default:
      return "Meta authorization could not be completed. Please try again."
  }
}

function getConnectionErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return "Meta Ads could not be connected. Please try again."
}
